import { useState, useEffect } from 'react';
import { logEventDirect } from '@/hooks/useEventLogger';
import { AnimatePresence } from 'framer-motion';
import { Share2, Check, Link2, MessageCircle, Video, Loader2, MoreHorizontal, Download, Send, Users } from 'lucide-react';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { toast } from 'sonner';
import { FriendPickerSheet } from './FriendPickerSheet';
import { useAuth } from '@/hooks/useAuth';
import { useShareCard } from '@/hooks/useShareCard';
import { ShareCardType } from './ShareCardCanvas';
import { downloadBrandedImage } from '@/utils/brandedDownload';
import { InlineFriendPicker } from './InlineFriendPicker';
import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import InstagramStories from '@/plugins/InstagramStories';
import { useShareTexts } from '@/hooks/useShareTexts';

interface ShareSheetProps {
  title: string;
  text?: string;
  url?: string;
  imageUrl?: string;
  images?: string[];
  videoUrl?: string;
  savedItemId?: string;
  pollId?: string; 
  lookId?: string;
  collectionId?: string;
  bodyGymLookId?: string;
  subtitle?: string;
  storeName?: string;
  userName?: string;
  /** Determines which share card + share text to generate. Falls back to inferring from IDs. */
  shareType?: 'product' | 'poll' | 'look' | 'collection' | 'celebrity' | 'style-profile' | 'price-guess' | 'outfit-challenge';
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDownload?: () => void;
  downloadFileName?: string;
  /** Extra data passed through to share card generation */
  shareCardData?: Record<string, unknown>;
  /** Custom z-index class forwarded to the bottom sheet (e.g. when rendered inside a lightbox) */
  zIndexClass?: string;
  /** Raw product metadata — enables "Send to Friends" even without a savedItemId */
  product?: {
    name: string;
    image_url: string;
    url?: string;
    price?: number | null;
    currency?: string;
    store?: string;
  };
}

type SharePlatform = 'whatsapp' | 'instagram';

export function ShareSheet({ title, text, url, imageUrl, images, videoUrl, savedItemId, pollId, lookId, collectionId, bodyGymLookId, subtitle, storeName, userName, shareType, children, open: controlledOpen, onOpenChange, onDownload, downloadFileName, shareCardData, zIndexClass, product }: ShareSheetProps) {
  const { user } = useAuth();
  const { getShareText } = useShareTexts();
  const [internalOpen, setInternalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSharingVideo, setIsSharingVideo] = useState(false);
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);
  const [loadingPlatform, setLoadingPlatform] = useState<SharePlatform | null>(null);
  const { generateShareCard } = useShareCard();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (onOpenChange) onOpenChange(value);
    if (!isControlled) setInternalOpen(value);
  };

  const generateShareUrl = () => {
    if (url) return url;

    const domain = import.meta.env.MODE === 'production'
      ? 'https://wntd.ai'
      : import.meta.env.MODE === 'staging'
      ? 'https://wntd.ai'
      : 'https://seyu.lovable.app';

    if (savedItemId) return `${domain}/saved/${savedItemId}`;
    if (collectionId) return `${domain}/collection/${collectionId}`;
    if (pollId) return `${domain}/vote?poll=${pollId}`;
    if (lookId) return `${domain}/try-on?look=${lookId}`;

    return domain;
  };

  const shareUrl = generateShareUrl();

  const generateShareText = () => {
    if (text) return text;
    const effectiveType = shareType || (savedItemId ? 'product' : pollId ? 'poll' : lookId ? 'look' : collectionId ? 'collection' : undefined);
    
    // Try dynamic template from database first
    const priceStr = subtitle ? ` – ${subtitle}` : '';
    const dynamic = getShareText(effectiveType, {
      title: title || '',
      price: priceStr,
      score: subtitle || '',
    });
    if (dynamic) return dynamic;

    // Hardcoded fallback if DB hasn't loaded yet
    switch (effectiveType) {
      case 'product':
        return `Check out ${title}${priceStr} on WNTD 🔗`;
      case 'poll':
        return `Help me pick! ${title || 'Vote on my style poll'} 👗`;
      case 'look':
        return `Check out this virtual try-on look! ✨`;
      case 'collection':
        return `Check out my "${title}" collection on WNTD 🛍️`;
      case 'celebrity':
        return `I found my celebrity twins! See who I matched with on WNTD ✨`;
      case 'style-profile':
        return `My style DNA: ${title || 'See my style profile'} on WNTD 🎨`;
      case 'price-guess':
        return `I scored ${title} on Price Guess! Can you beat me? 🎯`;
      case 'outfit-challenge':
        return `I scored ${subtitle || ''} on today's outfit challenge on WNTD! 👗`;
      default:
        return userName
          ? `Check out @${userName}'s style on WNTD 👀`
          : `Check out ${title} on WNTD`;
    }
  };

  const shareText = generateShareText();
  const canShareWithFriends = !!(savedItemId || pollId || lookId || collectionId || bodyGymLookId || (product?.name && product?.image_url));
  
  const handleShareVideoFile = async () => {
    if (!videoUrl) return;
    
    setIsSharingVideo(true);
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const file = new File([blob], 'animated-look.mp4', { type: 'video/mp4' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title, text: shareText, files: [file] });
        setOpen(false);
      } else {
        toast.error('Video file sharing not supported on this device');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Share error:', error);
        toast.error('Failed to share video');
      }
    } finally {
      setIsSharingVideo(false);
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy link');
    }
  };
  
  const handleShareViaDeepLink = (platform: SharePlatform) => {
    const encodedText = encodeURIComponent(`${shareText}\n${shareUrl}`);

    let url = '';
    switch (platform) {
      case 'whatsapp':
        // Use app URL scheme on native so WhatsApp opens directly, not in-app browser
        url = Capacitor.isNativePlatform()
          ? `whatsapp://send?text=${encodedText}`
          : `https://wa.me/?text=${encodedText}`;
        break;
      case 'instagram':
        url = Capacitor.isNativePlatform() ? 'instagram://' : 'https://www.instagram.com/';
        break;
    }

    if (Capacitor.isNativePlatform()) {
      // On iOS WKWebView, setting location.href to a custom scheme (whatsapp://, instagram://)
      // forwards it to the system URL handler, opening the native app directly.
      window.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    setOpen(false);
  };

  const getShareCardType = (): ShareCardType => {
    if (shareType) return shareType;
    if (savedItemId) return 'product';
    if (pollId) return 'poll';
    if (lookId) return 'look';
    if (collectionId) return 'collection';
    return 'product';
  };

  const handleShareWithImage = async (platform?: SharePlatform) => {
    if (platform) {
      setLoadingPlatform(platform);
    } else {
      setIsGeneratingCard(true);
    }

    let blob: Blob | null = null;
    try {
      blob = await generateShareCard({
        type: getShareCardType(),
        title,
        imageUrl,
        images,
        subtitle,
        storeName,
        userName,
        ...(shareCardData as Record<string, unknown> || {}),
      } as import('./ShareCardCanvas').ShareCardData);
    } catch (error) {
      console.warn('Share card generation failed, will use fallback:', error);
    }

    try {
      if (blob) {
        const file = new File([blob], 'share-card.png', { type: 'image/png' });

        if (platform && Capacitor.isNativePlatform()) {
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob!);
          });

          if (platform === 'instagram') {
            try {
              await InstagramStories.shareToStories({ imageBase64: base64 });
              setOpen(false);
              return;
            } catch {
              await Clipboard.write({ image: base64 });
              toast.success('Image copied — paste it in Instagram to share!', { duration: 3000 });
              handleShareViaDeepLink(platform);
              return;
            }
          } else {
            await Clipboard.write({ image: base64 });
            toast.success('Image copied — paste it to share!', { duration: 3000 });
            handleShareViaDeepLink(platform);
            return;
          }
        } else if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title, text: shareText, files: [file] });
          setOpen(false);
          return;
        }
      }

      // Fallback: no blob or can't share files — use text-only share
      if (platform) {
        handleShareViaDeepLink(platform);
      } else if (navigator.share) {
        await navigator.share({ title, text: shareText, url: shareUrl });
        setOpen(false);
      } else if (blob) {
        // Desktop fallback: download the image
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'share-card.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Share error:', error);
        // Final fallback for "More" — text share
        if (!platform && navigator.share) {
          try {
            await navigator.share({ title, text: shareText, url: shareUrl });
            setOpen(false);
          } catch {}
        } else if (platform) {
          handleShareViaDeepLink(platform);
        }
      }
    } finally {
      setLoadingPlatform(null);
      setIsGeneratingCard(false);
    }
  };

  const handleNativeShare = async () => {
    await handleShareWithImage();
  };

  // Track share activity when the sheet opens
  useEffect(() => {
    if (open && user?.id) {
      logEventDirect('share_initiated', 'engagement', {
        content_type: savedItemId ? 'saved_item' : pollId ? 'poll' : lookId ? 'look' : collectionId ? 'collection' : 'other',
        content_id: savedItemId || pollId || lookId || collectionId || null,
        title: title || null,
      }, user.id);
    }
  }, [open]);
  
  const handleTriggerClick = () => {
    setOpen(true);
  };

  return (
    <>
      {!isControlled && (
        children ? (
          <div onClick={handleTriggerClick}>
            {children}
          </div>
        ) : (
          <button 
            onClick={handleTriggerClick}
            className="glass-bubble w-10 h-10"
          >
            <Share2 className="w-[18px] h-[18px] header-glass-icon" strokeWidth={2} />
          </button>
        )
      )}
      
      <AnimatePresence>
        {open && (
          <DraggableBottomSheet
            open={open}
            onOpenChange={setOpen}
            title="Share"
            height="auto"
            zIndexClass={zIndexClass}
          >
            <div className="px-4 pb-4">
              {/* Preview Card */}
              <div className="flex items-center gap-3 p-3 bg-surface-2 rounded-2xl mb-5">
                {imageUrl && (
                  <img 
                    src={imageUrl} 
                    alt={title}
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground line-clamp-2 text-sm">
                    {title}
                  </h4>
                  {subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>

               {/* Section 1: Send to Friends on WNTD */}
              {canShareWithFriends && user && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Send to Friends on WNTD
                  </p>
                   <InlineFriendPicker
                     savedItemId={savedItemId}
                     pollId={pollId}
                     lookId={lookId}
                     collectionId={collectionId}
                     bodyGymLookId={bodyGymLookId}
                     product={product}
                     itemName={title}
                     onSent={() => setOpen(false)}
                   />
                </div>
              )}

              {/* Section 2: Share externally */}
              <div>
                {canShareWithFriends && user && (
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Share externally
                  </p>
                )}
                
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <button
                    onClick={() => handleShareWithImage('whatsapp')}
                    disabled={loadingPlatform !== null || isGeneratingCard}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-2xl hover:bg-surface-2 active:scale-[0.97] transition-all disabled:opacity-50"
                  >
                    <div className="w-12 h-12 rounded-full bg-[hsl(142,70%,49%)]/10 flex items-center justify-center">
                      {loadingPlatform === 'whatsapp' ? (
                        <Loader2 className="w-5 h-5 text-[hsl(142,70%,49%)] animate-spin" />
                      ) : (
                        <MessageCircle className="w-5 h-5 text-[hsl(142,70%,49%)]" />
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground leading-tight">WhatsApp</span>
                  </button>
                  
                  <button
                    onClick={() => handleShareWithImage('instagram')}
                    disabled={loadingPlatform !== null || isGeneratingCard}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-2xl hover:bg-surface-2 active:scale-[0.97] transition-all disabled:opacity-50"
                  >
                    <div className="w-12 h-12 rounded-full bg-[hsl(330,70%,50%)]/10 flex items-center justify-center">
                      {loadingPlatform === 'instagram' ? (
                        <Loader2 className="w-5 h-5 text-[hsl(330,70%,50%)] animate-spin" />
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" stroke="hsl(330,70%,50%)" strokeWidth="2"/><circle cx="12" cy="12" r="5" stroke="hsl(330,70%,50%)" strokeWidth="2"/><circle cx="17.5" cy="6.5" r="1.5" fill="hsl(330,70%,50%)"/></svg>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground leading-tight">Instagram</span>
                  </button>

                  <button
                    onClick={handleNativeShare}
                    disabled={isGeneratingCard || loadingPlatform !== null}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-2xl hover:bg-surface-2 active:scale-[0.97] transition-all disabled:opacity-50"
                  >
                    <div className="w-12 h-12 rounded-full bg-surface-3 flex items-center justify-center">
                      {isGeneratingCard ? (
                        <Loader2 className="w-5 h-5 text-foreground animate-spin" />
                      ) : (
                        <MoreHorizontal className="w-5 h-5 text-foreground" />
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground leading-tight">More</span>
                  </button>
                </div>

                {/* Copy Link */}
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 p-3 bg-surface-2 rounded-2xl hover:bg-surface-3 active:scale-[0.98] transition-all"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-priceDrop" />
                  ) : (
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium text-foreground">
                    {copied ? 'Copied!' : 'Copy Link'}
                  </span>
                </button>

                {/* Share Video */}
                {videoUrl && (
                  <button
                    onClick={handleShareVideoFile}
                    disabled={isSharingVideo}
                    className="w-full flex items-center justify-center gap-2 p-3 mt-2 bg-surface-2 rounded-2xl hover:bg-surface-3 active:scale-[0.98] transition-all disabled:opacity-50"
                  >
                    <Video className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      {isSharingVideo ? 'Preparing video...' : 'Share Video'}
                    </span>
                  </button>
                )}

                {/* Save Image */}
                {(onDownload || imageUrl) && (
                  <button
                    onClick={async () => {
                      if (onDownload) {
                        onDownload();
                      } else if (imageUrl) {
                        try {
                          await downloadBrandedImage(imageUrl, downloadFileName || `image-${Date.now()}.png`);
                          toast.success('Image saved');
                        } catch {
                          toast.error('Failed to save image');
                        }
                      }
                    }}
                    className="w-full flex items-center gap-3 p-3 mt-2 bg-surface-2 rounded-2xl hover:bg-surface-3 active:scale-[0.98] transition-all"
                  >
                    <Download className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Save Image</span>
                  </button>
                )}
              </div>
            </div>
          </DraggableBottomSheet>
        )}
      </AnimatePresence>
    </>
  );
}
